import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Flame, Droplets, TrendingDown, DollarSign } from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function TokenomicsWidget() {
    const [burn, setBurn] = useState(null);
    const [weekly, setWeekly] = useState(null);
    const [halving, setHalving] = useState(null);
    const [price, setPrice] = useState(null);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                const [b, w, h, p] = await Promise.all([
                    fetch(`${API_URL}/api/burn/stats`).then(r => r.json()),
                    fetch(`${API_URL}/api/burn/weekly`).then(r => r.json()),
                    fetch(`${API_URL}/api/b4p/stats`).then(r => r.json()),
                    fetch(`${API_URL}/api/pricing/b4p-usd`).then(r => r.json()),
                ]);
                if (cancelled) return;
                setBurn(b?.burn || null);
                setWeekly(w);
                setHalving(h);
                setPrice(p);
            } catch (e) {
                console.error('TokenomicsWidget load failed', e);
            }
        }
        load();
        const id = setInterval(load, 60000);
        return () => { cancelled = true; clearInterval(id); };
    }, []);

    const halvingProgress = halving && halving.next_halving_at
        ? Math.min(100, (halving.total_distributed / halving.next_halving_at) * 100)
        : 100;
    const halvingRemaining = halving && halving.next_halving_at
        ? Math.max(0, halving.next_halving_at - halving.total_distributed)
        : 0;
    const halvingFinalPhase = halving && !halving.next_halving_at;

    return (
        <section className="py-12 border-t border-white/5" data-testid="tokenomics-widget">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
                    <div>
                        <h2 className="font-serif-luxury text-3xl md:text-4xl text-white tracking-tight divider-gold">
                            B4P <span className="text-gold-gradient">Tokenomics</span> <span className="font-serif-luxury-italic text-white/70">Live</span>
                        </h2>
                        <p className="text-sm text-white/50 mt-3">
                            Hybrid USD-Pegged rewards · Auto buy-back &amp; burn · Halving phase {halving?.current_phase ?? 1}
                        </p>
                    </div>
                    <Link to="/burn" className="text-xs text-[#FFD700] hover:underline tracking-wide" data-testid="tokenomics-widget-burn-link">
                        Voir détails →
                    </Link>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                    {/* Total Burn */}
                    <Card className="bg-white/5 border-white/10" data-testid="widget-total-burn">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-2 text-[#FF4500] mb-2">
                                <Flame className="w-4 h-4" />
                                <span className="text-xs uppercase tracking-wider font-semibold">Total Burned</span>
                            </div>
                            <div className="font-mono text-2xl font-bold text-white">
                                {burn ? burn.total_burned_b4p.toLocaleString('en-US', { maximumFractionDigits: 2 }) : '...'}
                            </div>
                            <div className="text-xs text-white/40 mt-0.5">B4P burned ({burn?.burn_count ?? 0} events)</div>
                        </CardContent>
                    </Card>

                    {/* Weekly Burn */}
                    <Card className="bg-white/5 border-white/10" data-testid="widget-weekly-burn">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-2 text-[#FFD700] mb-2">
                                <TrendingDown className="w-4 h-4" />
                                <span className="text-xs uppercase tracking-wider font-semibold">7d Burn</span>
                            </div>
                            <div className="font-mono text-2xl font-bold text-white">
                                {weekly ? weekly.weekly_burned_b4p.toLocaleString('en-US', { maximumFractionDigits: 2 }) : '...'}
                            </div>
                            <div className="text-xs text-white/40 mt-0.5">B4P / week</div>
                        </CardContent>
                    </Card>

                    {/* Liquidity Reserve */}
                    <Card className="bg-white/5 border-white/10" data-testid="widget-liquidity-reserve">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-2 text-[#00FF66] mb-2">
                                <Droplets className="w-4 h-4" />
                                <span className="text-xs uppercase tracking-wider font-semibold">DEX Reserve</span>
                            </div>
                            <div className="font-mono text-2xl font-bold text-white">
                                {burn ? Number(burn.total_liquidity_reserve_eth).toFixed(5) : '...'}
                            </div>
                            <div className="text-xs text-white/40 mt-0.5">ETH (Aerodrome pool)</div>
                        </CardContent>
                    </Card>

                    {/* B4P Price */}
                    <Card className="bg-white/5 border-white/10" data-testid="widget-b4p-price">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-2 text-[#9B6FE8] mb-2">
                                <DollarSign className="w-4 h-4" />
                                <span className="text-xs uppercase tracking-wider font-semibold">B4P/USD</span>
                            </div>
                            <div className="font-mono text-2xl font-bold text-white">
                                ${price ? Number(price.price).toFixed(price.price < 0.01 ? 6 : 4) : '...'}
                            </div>
                            <div className="text-xs text-white/40 mt-0.5">
                                src: {price?.source || '...'}
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Halving progress bar */}
                <div className="mt-6 bg-white/5 border border-white/10 rounded-lg p-4" data-testid="widget-halving">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                            <span className="text-xs uppercase tracking-wider font-semibold text-[#FFD700]">
                                Halving Phase {halving?.current_phase ?? 1}
                            </span>
                            <span className="text-[10px] text-white/40">×{halving?.current_multiplier ?? 1.0} reward multiplier</span>
                        </div>
                        <span className="text-xs text-white/60 font-mono">
                            {halving
                                ? halvingFinalPhase
                                    ? `${halving.total_distributed.toLocaleString()} B4P · final phase`
                                    : `${halving.total_distributed.toLocaleString()} / ${halving.next_halving_at.toLocaleString()} B4P`
                                : '...'}
                        </span>
                    </div>
                    <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-[#FFD700] to-[#FF4500] transition-all duration-700"
                            style={{ width: `${halvingProgress}%` }}
                            data-testid="halving-progress-bar"
                        />
                    </div>
                    <div className="text-[10px] text-white/40 mt-1.5">
                        {halvingFinalPhase
                            ? 'Halving final atteint — multiplier minimum (0.05×) actif'
                            : halvingRemaining > 0
                                ? `${halvingRemaining.toLocaleString()} B4P avant le prochain halving (rewards ÷ 2)`
                                : 'Halving imminent — rewards seront divisés par 2'}
                    </div>
                </div>
            </div>
        </section>
    );
}
